import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, Award, User, Heart, Receipt, FileText, Briefcase, Bookmark, Hexagon } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { getProfile, type IndividualProfile, type CompanyProfile } from '@/services/profileService'

const INDIVIDUAL_TAB_GROUPS = [
  {
    label: 'LEARNING',
    items: [
      { key: 'courses', label: 'คอร์สเรียนของฉัน', icon: BookOpen },
      { key: 'skills', label: 'สกิลของฉัน', icon: Hexagon },
      { key: 'certificates', label: 'ประกาศนียบัตร', icon: Award },
      { key: 'wishlist', label: 'คอร์สที่ถูกใจ', icon: Heart },
    ],
  },
  {
    label: 'JOBS',
    items: [
      { key: 'my-applications', label: 'ใบสมัครงานของฉัน', icon: Briefcase },
      { key: 'saved-jobs', label: 'งานที่บันทึกไว้', icon: Bookmark },
    ],
  },
  {
    label: 'ACCOUNT',
    items: [
      { key: 'profile', label: 'โปรไฟล์ของฉัน', icon: User },
      { key: 'orders', label: 'ประวัติคำสั่งซื้อ', icon: Receipt },
      { key: 'inhouse', label: 'คำขอ In-house', icon: FileText },
    ],
  },
]

// บัญชีบริษัท — 2 กลุ่ม: สรรหาบุคลากร / บริษัท
// กลุ่ม "COURSE MANAGEMENT" (คอร์สของบริษัท/พนักงานและสิทธิ์เรียน/ใบประกาศนียบัตรพนักงาน) ถูกเอาออกแล้ว
// ยังไม่มีหน้าจริงรองรับ (เคยเป็นแค่ placeholder "เร็วๆ นี้") — ทำใหม่เป็นเมนูเมื่อพร้อมจริง
const COMPANY_TAB_GROUPS = [
  {
    label: 'RECRUITMENT',
    // "ผู้สมัครงาน" ถูกเอาออกจากเมนูแล้ว — ดูผู้สมัครทีละตำแหน่งผ่านปุ่ม "ดูผู้สมัคร" ในแต่ละแถวของหน้านี้แทน (ไม่ทำหน้ารวมทุกตำแหน่ง)
    items: [
      { key: 'job-postings', label: 'ประกาศงานของบริษัท', icon: Briefcase },
    ],
  },
  {
    label: 'COMPANY',
    items: [
      { key: 'profile', label: 'โปรไฟล์บริษัท', icon: User },
      { key: 'orders', label: 'ประวัติคำสั่งซื้อ', icon: Receipt },
      { key: 'inhouse', label: 'คำขอ In-house', icon: FileText },
    ],
  },
]

function isIndividual(p: IndividualProfile | CompanyProfile): p is IndividualProfile {
  return 'first_name' in p
}

export function useTabGroups() {
  const customerType = useAuthStore((s) => s.customerType)
  return customerType === 'COMPANY' ? COMPANY_TAB_GROUPS : INDIVIDUAL_TAB_GROUPS
}

// Shell ใช้ร่วมกันระหว่าง /account (ทุก tab) และ /profile/edit
// เพื่อให้ sidebar + header + container เหมือนกันทุกพิกเซล — เปลี่ยนแค่ content ฝั่งขวา
// เมนู sidebar สลับตาม customerType (บุคคลทั่วไป vs บริษัท) — ดู useTabGroups()
// บัญชีบริษัท: แสดงเต็มหน้าจอ ไม่มีกรอบการ์ดล้อมรอบ (ต่างจากบุคคลทั่วไปที่ยังเป็นการ์ดลอยกลางหน้าเหมือนเดิม)
export default function AccountShell({
  activeTab,
  title,
  subtitle,
  hideHeader,
  children,
}: {
  activeTab: string
  title: string
  subtitle?: string
  hideHeader?: boolean   // หน้าที่มีหัวของตัวเองอยู่แล้ว (เช่น หน้าผู้สมัครงานรายตำแหน่ง) ไม่ต้องซ้อนหัว "บัญชีของฉัน"
  children: ReactNode
}) {
  const [profile, setProfile] = useState<IndividualProfile | CompanyProfile | null>(null)
  const tabGroups = useTabGroups()
  const isCompany = useAuthStore((s) => s.customerType === 'COMPANY')

  useEffect(() => {
    getProfile().then(setProfile).catch(() => {})
  }, [])

  const displayName = profile ? (isIndividual(profile) ? `${profile.first_name} ${profile.last_name}` : profile.company_name) : ''

  const sidebar = (
    // ไม่ sticky — เมนูอยู่กับที่ เลื่อนหน้าแล้วเลื่อนออกไปพร้อมเนื้อหา (ไม่ลอยตามจอ) พื้นหลังยืดเต็มความสูงการ์ด/หน้าตาม flex ของตัวครอบ
    <aside className="hidden w-56 shrink-0 border-r border-[#E7E7ED] bg-[#F5F3FC] lg:block">
      <nav className="space-y-8 p-6">
        {tabGroups.map((group, gi) => (
          <div key={group.label} className={gi > 0 ? 'border-t border-[#E1DCF5] pt-8' : ''}>
            <p className="mb-3 px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#69738A]">
              {group.label}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const active = activeTab === item.key
                return (
                  <Link
                    key={item.key}
                    to={`/account?tab=${item.key}`}
                    className={`group flex items-center gap-3 border-l-2 py-2.5 pl-[10px] pr-3 text-sm transition-colors ${
                      active
                        ? 'border-accent bg-accent/10 font-medium text-accent'
                        : 'border-transparent text-[#69738A] hover:border-[#D9D2F0] hover:text-[#19172A]'
                    }`}
                  >
                    <item.icon className={`h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 ${active ? 'text-accent' : 'text-[#69738A]'}`} />
                    {item.label}
                  </Link>
                )
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  )

  const header = hideHeader ? null : (
    <div className="mb-8 flex items-center justify-between border-b border-[#E7E7ED] pb-6">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#69738A]">บัญชีของฉัน</p>
        <h1 className="mt-1 text-2xl font-semibold text-[#19172A]">{title}</h1>
        {subtitle && <p className="mt-1.5 text-sm text-[#69738A]">{subtitle}</p>}
      </div>
      {displayName && (
        <div className="hidden items-center gap-2.5 sm:flex">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/10 text-xs font-semibold text-accent">
            {displayName[0]}
          </div>
          <span className="text-sm text-[#69738A]">{displayName}</span>
        </div>
      )}
    </div>
  )

  const mobileTabs = (
    <div className="mb-8 flex gap-6 overflow-x-auto border-b border-[#E7E7ED] lg:hidden">
      {tabGroups.flatMap((g) => g.items).map((item) => {
        const active = activeTab === item.key
        return (
          <Link
            key={item.key}
            to={`/account?tab=${item.key}`}
            className={`shrink-0 border-b-2 pb-3 text-sm transition-colors ${
              active ? 'border-accent font-medium text-accent' : 'border-transparent text-[#69738A]'
            }`}
          >
            {item.label}
          </Link>
        )
      })}
    </div>
  )

  // บัญชีบริษัท — เต็มความกว้างจอ ไม่มีกรอบการ์ดล้อมรอบ (ต่างจากบุคคลทั่วไปที่ยังเป็นการ์ดลอยกลางหน้า)
  // ใช้ document flow ปกติเหมือนหน้า Courses/Jobs (min-h-screen + pt ตามความสูง navbar จริง) มี Footer ต่อท้ายตามปกติ
  if (isCompany) {
    return (
      <div className="min-h-screen bg-[#FAFAFC] pt-16">
        <div className="px-4 lg:hidden md:px-8">{mobileTabs}</div>
        <div className="flex min-h-[calc(100vh-4rem)]">
          {sidebar}
          <div className="min-w-0 flex-1 px-4 pb-16 pt-6 md:px-10">
            {header}
            {children}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#FAFAFC] pt-24 pb-20">
      <div className="w-full px-4 md:px-8">
        {mobileTabs}

        <div className="flex border border-[#E7E7ED] bg-white" style={{ borderRadius: 8 }}>
          {sidebar}
          <div className="min-w-0 flex-1 p-6 md:p-8">
            {header}
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
